import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { SceneFormValues } from '../schemas';

import { useTextActions } from '@/features/editor/hooks/useTextActions';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { NarrativeSummary, Scene } from '@/lib/types';

/**
 * Hook to handle writing a scene (navigation + optional creation).
 */
export function useWriteScene() {
  const { firestore, novel, narrativeDao } = useNovelRepository();
  const { user } = useFirebase();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { createNewText } = useTextActions();
  const [isCreatingContent, setIsCreatingContent] = useState(false);
  const [pendingSceneId, setPendingSceneId] = useState<string | null>(null);

  const writeScene = async (scene: NarrativeSummary) => {
    if (!firestore || !novel.id || !user?.uid) return;

    if (scene.mainContentId) {
      navigate({
        to: '/editor',
        search: { textId: scene.mainContentId },
      });
      return;
    }

    setPendingSceneId(scene.id);
    setIsCreatingContent(true);
    try {
      // 1. Create new text (Content) using the modern action hook
      const contentId = await createNewText(scene.name, [scene.id]);

      if (!contentId) throw new Error('Failed to create content');

      const now = new Date().toISOString();
      const updatedScene = {
        ...scene,
        mainContentId: contentId,
        updatedAt: now,
      };

      // 2. Update Scene (DAO handles mirroring)
      await narrativeDao?.updateNarrativeItem(novel.ownerId, novel, 'scenes', scene.id, updatedScene);

      toast({
        title: t('common.success'),
        description: t('editor.content_created'),
      });

      // 4. Navigate
      navigate({
        to: '/editor',
        search: { textId: contentId },
      });
    } catch (error) {
      console.error('Failed to create scene content:', error);
      toast({
        title: t('common.error'),
        description: t('editor.content_creation_failed'),
        variant: 'destructive',
      });
    } finally {
      setIsCreatingContent(false);
      setPendingSceneId(null);
    }
  };

  const createFullScene = async (name?: string, chapterId?: string) => {
    if (!firestore || !novel.id || !user?.uid) return null;

    setIsCreatingContent(true);
    try {
      const sceneName = name || t('editor.untitled_scene');
      const now = new Date().toISOString();

      // 1. Create text (Content)
      const id = uuidv4();
      const contentId = await createNewText(sceneName, [id]);

      if (!contentId) throw new Error('Failed to create content');

      // 2. Create Scene (DAO handles mirroring)
      const newScene: Scene = {
        id,
        name: sceneName,
        ...(chapterId ? { chapterId } : {}),
        mainContentId: contentId,
        status: { phase: 'idle', attempts: 0 },
        shortDescription: '',
        images: [],
        primaryImageId: null,
        audit: {
          createdAt: now,
          updatedAt: now,
        },
      };

      await narrativeDao?.setNarrativeItem(novel.ownerId, novel, 'scenes', newScene);

      toast({
        title: t('common.success'),
        description: t('editor.scene_created'),
      });

      // 4. Navigate
      navigate({
        to: '/editor',
        search: { textId: contentId },
      });

      return contentId;
    } catch (error) {
      console.error('Failed to create full scene:', error);
      toast({
        title: t('common.error'),
        description: t('editor.scene_creation_failed'),
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsCreatingContent(false);
    }
  };

  return { writeScene, createFullScene, isCreatingContent, pendingSceneId };
}

/**
 * Hook to handle scene deletion.
 */
export function useDeleteScene(sceneId: string | null | undefined) {
  const {
    novel,
    narrativeDao,
  } = useNovelRepository();
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteScene = async () => {
    if (!novel.id || !sceneId) return;

    setIsDeleting(true);
    try {
      if (!narrativeDao) return;
      await narrativeDao.deleteNarrativeItem(novel.ownerId, novel, 'scenes', sceneId);

      toast({
        title: t('entity_form.scenes.delete_success_title'),
        description: t('entity_form.scenes.delete_success_desc'),
      });

      navigate({ to: '/scenes' });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: t('entity_form.common.delete_failed_title'),
        description: t('entity_form.scenes.delete_failed_desc'),
        variant: 'destructive',
      });
      setIsDeleting(false);
    }
  };

  return { deleteScene, isDeleting };
}

/**
 * Hook to handle scene updates.
 */
export function useUpdateScene(sceneId: string | null | undefined, _existingScene?: Scene | null) {
  void _existingScene;
  const {
    novel,
    narrativeDao,
  } = useNovelRepository();
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const updateScene = async (values: SceneFormValues) => {
    if (!novel.id || !sceneId) return;

    setIsSaving(true);
    try {
      const updateData = {
        ...values,
        id: sceneId,
        shortDescription: values.description ? values.description.substring(0, 150) : '',
      };

      if (!narrativeDao) return;
      await narrativeDao.updateNarrativeItem(novel.ownerId, novel, 'scenes', sceneId, updateData);

      toast({
        title: t('entity_form.scenes.update_success_title'),
        description: t('entity_form.scenes.update_success_desc', { name: values.name }),
      });

      navigate({ to: '/scenes' });
    } catch (error) {
      console.error('Update error:', error);
      toast({
        title: t('entity_form.common.update_failed_title'),
        description: t('entity_form.scenes.update_failed_desc'),
        variant: 'destructive',
      });
      setIsSaving(false);
    }
  };

  return { updateScene, isSaving };
}
